import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EntityLink } from '../entities/EntityLink';
import { Check, Plus, Search, Trash2, X } from 'lucide-react';
import {
    PerspectiveValueType,
    SitePerspectiveType,
    from,
    is,
    or,
    type SlotField,
    type SlotReference,
} from '@the-real-insight/tri-model';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import NumericInput from '../components/NumericInput';
import { NumericKind } from '../components/numericFormat';
import { userAPI } from '../UserAPI';
import { monthsOf, toIsoMonths } from './durationValue';
import { formatValue } from './perspectiveValues';
import { knownEntityType } from '../entities/entityTypes';
import { LABEL_FIELDS, describeRecord, useReferenceLabel } from './referenceLabel';

/**
 * Die Eingaben für Slots, die kein einzelner Wert sind (tri-model `AttributeSlot`):
 *
 *  - **Objekt** — mehrere Felder, die zusammen gehören (GRZ und GFZ, Beginn und Ende).
 *  - **Liste** — Einträge mit Anlegen und Entfernen; jeder Eintrag hat seinen Schlüssel und seine
 *    Zeile in der Historie.
 *  - **Referenz** — eine Entität der Datenbank, gesucht und verlinkt.
 *  - **Gebunden** — der Slot hängt an Entitäten, die ein Dokument geliefert hat; bei mehreren zeigt
 *    er eine Zeile je Instanz.
 */

/** Was das Formular zum Schreiben von Listen und Instanzwerten braucht. */
export interface SlotActions {
    changeListItem: (
        type: SitePerspectiveType,
        name: string,
        change: { add: Record<string, any> } | { remove: string },
        comment?: string,
    ) => Promise<void>;
    saveInstanceValue: (type: SitePerspectiveType, name: string, ref: SlotReference, value: any, comment?: string) => Promise<void>;
}

/** Was der Server zu einem gebundenen Slot liefert (`resolved` an der Perspektive). */
export interface ResolvedSlot {
    className: string;
    path: string;
    cardinality: 'one' | 'many';
    source?: { documentName?: string; analysisId?: string };
    instances: Array<{ ref: SlotReference; label: string; value: any; link: string; missing?: boolean }>;
}

const NUMERIC_KIND: Partial<Record<PerspectiveValueType, NumericKind>> = {
    [PerspectiveValueType.money]: 'money',
    [PerspectiveValueType.decimal]: 'decimal',
    [PerspectiveValueType.percent]: 'percent',
    [PerspectiveValueType.integer]: 'integer',
};

const UNSET = '__unset__';

const labelOf = (field: { label?: { de: string; en: string }; name?: string }, lang: 'de' | 'en') =>
    field.label?.[lang] ?? field.label?.de ?? field.name ?? '';

const isBlank = (value: any) => value === null || value === undefined || value === '';

export { EntityLink };

/** Woran eine Entität erkannt wird — die Felder, in denen gesucht wird. */
const SEARCH_FIELDS = LABEL_FIELDS;

const searchFieldsOf = (className: string): string[] => {
    const type = knownEntityType(className) as any;
    const names = new Set(((type?.fields ?? []) as any[]).map((field) => field.name));
    return SEARCH_FIELDS.filter((name) => names.has(name));
};

/** Ein Suchmuster ohne Gross- und Kleinschreibung, aus dem, was jemand tippt. */
const caseless = (text: string) =>
    text
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        .replace(/[a-zäöüß]/gi, (letter) => `[${letter.toLowerCase()}${letter.toUpperCase()}]`);

/** Konkrete Klassen, in denen gesucht wird: eine abstrakte wie `Person` über ihre Unterklassen. */
const concreteOf = (className: string): string[] => (className === 'Person' ? ['LegalPerson', 'NaturalPerson'] : [className]);

const describe = describeRecord;

/**
 * Eine Entität auswählen: suchen, wählen, verlinkt zeigen.
 *
 * Gesucht wird in den Feldern, an denen ein Mensch die Sache erkennt (Name, Firma, Aktenzeichen). Eine
 * Klasse ohne solche Felder lässt sich hier nicht suchen — sie zeigt dann nur, was schon gewählt ist.
 */
export function ReferencePicker({
    target,
    value,
    onChange,
    disabled,
}: {
    target: string;
    value: SlotReference | string | null | undefined;
    onChange: (next: SlotReference | null) => void;
    disabled?: boolean;
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [text, setText] = useState('');
    const [found, setFound] = useState<Array<{ ref: SlotReference; label: string }>>([]);
    const [searching, setSearching] = useState(false);
    const current: SlotReference | null =
        value && typeof value === 'object' ? value : typeof value === 'string' && value ? { _id: value, type: target } : null;
    const searchable = concreteOf(target).some((name) => searchFieldsOf(name).length > 0);
    const label = useReferenceLabel(current);

    useEffect(() => {
        if (!open || text.trim().length < 2) {
            setFound([]);
            return;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            setSearching(true);
            try {
                const pattern = caseless(text.trim());
                const results = await Promise.all(
                    concreteOf(target).map(async (className) => {
                        const fields = searchFieldsOf(className);
                        const type = knownEntityType(className) as any;
                        if (!type || fields.length === 0) return [];
                        const filter = fields.length === 1 ? is(fields[0]).equalRegExp(pattern) : or(...fields.map((field) => is(field).equalRegExp(pattern)));
                        const rows = await userAPI.query(from(type.type).where(filter), 0, 10);
                        return rows.map((row: any) => ({ ref: { _id: String(row._id), type: className }, label: describe(row) }));
                    }),
                );
                if (!cancelled) setFound(results.flat().slice(0, 12));
            } catch (error) {
                console.error('Entity search failed', error);
                if (!cancelled) setFound([]);
            } finally {
                if (!cancelled) setSearching(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [open, text, target]);

    if (!open) {
        return (
            <div className="flex flex-wrap items-center gap-2 text-sm">
                {current ? <EntityLink reference={current} label={label} /> : <span className="text-muted-foreground">—</span>}
                {!disabled && searchable ? (
                    <Button type="button" variant="link" size="sm" className="h-auto px-0" onClick={() => setOpen(true)}>
                        {current ? t('portfolioDashboard.slots.change') : t('portfolioDashboard.slots.choose')}
                    </Button>
                ) : null}
                {!disabled && current ? (
                    <Button type="button" variant="ghost" size="sm" className="h-6 px-1" aria-label={t('portfolioDashboard.slots.clear')} onClick={() => onChange(null)}>
                        <X className="h-3 w-3" aria-hidden />
                    </Button>
                ) : null}
            </div>
        );
    }

    return (
        <div className="space-y-1">
            <div className="flex items-center gap-1">
                <Search className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                <Input
                    autoFocus
                    value={text}
                    placeholder={t('portfolioDashboard.slots.searchPlaceholder')}
                    aria-label={t('portfolioDashboard.slots.search')}
                    onChange={(event) => setText(event.target.value)}
                    className="h-8"
                />
                <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)} aria-label={t('global.cancel')}>
                    <X className="h-3.5 w-3.5" aria-hidden />
                </Button>
            </div>
            {searching ? <div className="text-xs text-muted-foreground">{t('global.loading')} …</div> : null}
            {found.length > 0 ? (
                <ul className="max-h-48 overflow-y-auto rounded-md border text-sm">
                    {found.map((entry) => (
                        <li key={`${entry.ref.type}:${entry.ref._id}`}>
                            <button
                                type="button"
                                className="w-full px-2 py-1 text-left hover:bg-muted"
                                onClick={() => {
                                    onChange(entry.ref);
                                    setOpen(false);
                                    setText('');
                                }}
                            >
                                {entry.label}
                                <span className="ml-2 text-xs text-muted-foreground">{(knownEntityType(entry.ref.type) as any)?.type?.label?.de ?? entry.ref.type}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : text.trim().length >= 2 && !searching ? (
                <div className="text-xs text-muted-foreground">{t('portfolioDashboard.slots.nothingFound')}</div>
            ) : null}
        </div>
    );
}

/**
 * Ein Feld eines Objekts oder eines Listeneintrags, nach seinem Typ.
 *
 * Schreibt beim Verlassen, wie die Felder des Formulars: Ein Objekt wird als Ganzes geschrieben,
 * aber erst, wenn eines seiner Felder fertig ist.
 */
export function FieldInput({
    field,
    value,
    onCommit,
    disabled,
}: {
    field: SlotField | { name: string; valueType: PerspectiveValueType; target?: string; label?: { de: string; en: string } };
    value: any;
    onCommit: (next: any) => void;
    disabled?: boolean;
}) {
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.startsWith('en') ? 'en' : 'de';
    const [text, setText] = useState<string | null>(null);
    const label = labelOf(field, lang);
    const kind = NUMERIC_KIND[field.valueType];

    if (field.valueType === PerspectiveValueType.reference) {
        return <ReferencePicker target={field.target ?? 'Entity'} value={value} onChange={onCommit} disabled={disabled} />;
    }
    if (kind) {
        return <NumericInput kind={kind} value={value ?? null} disabled={disabled} ariaLabel={label} onChange={(next) => onCommit(next)} />;
    }
    if (field.valueType === PerspectiveValueType.duration) {
        return <NumericInput kind="integer" value={monthsOf(value)} disabled={disabled} ariaLabel={label} onChange={(months) => onCommit(toIsoMonths(months))} />;
    }
    if (field.valueType === PerspectiveValueType.boolean) {
        return (
            <Select
                value={value === true ? 'true' : value === false ? 'false' : UNSET}
                disabled={disabled}
                onValueChange={(next) => onCommit(next === UNSET ? null : next === 'true')}
            >
                <SelectTrigger aria-label={label} className="h-8">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value={UNSET}>{t('portfolioDashboard.perspectiveForm.unset')}</SelectItem>
                    <SelectItem value="true">{t('portfolioDashboard.perspectiveForm.yes')}</SelectItem>
                    <SelectItem value="false">{t('portfolioDashboard.perspectiveForm.no')}</SelectItem>
                </SelectContent>
            </Select>
        );
    }
    const date = field.valueType === PerspectiveValueType.date;
    const stored = isBlank(value) ? '' : date ? new Date(value).toISOString().slice(0, 10) : String(value);
    return (
        <Input
            value={text ?? stored}
            type={date ? 'date' : 'text'}
            aria-label={label}
            disabled={disabled}
            className="h-8"
            onChange={(event) => setText(event.target.value)}
            onBlur={(event) => {
                setText(null);
                if (event.target.value !== stored) onCommit(event.target.value === '' ? null : event.target.value);
            }}
        />
    );
}

/** Ein literales Objekt: seine Felder nebeneinander, geschrieben als Ganzes. */
export function ObjectSlotEditor({
    fields,
    value,
    onCommit,
    disabled,
}: {
    fields: SlotField[];
    value: Record<string, any> | null | undefined;
    onCommit: (next: Record<string, any> | null) => void;
    disabled?: boolean;
}) {
    const { i18n } = useTranslation();
    const lang = i18n.language?.startsWith('en') ? 'en' : 'de';
    return (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {fields.map((field) => (
                <label key={field.name} className="space-y-0.5 text-xs text-muted-foreground">
                    <span>
                        {labelOf(field, lang)}
                        {field.unit ? ` (${field.unit})` : ''}
                    </span>
                    <FieldInput
                        field={field}
                        value={value?.[field.name]}
                        disabled={disabled}
                        onCommit={(next) => {
                            const merged = { ...(value ?? {}), [field.name]: next };
                            Object.keys(merged).forEach((key) => isBlank(merged[key]) && delete merged[key]);
                            onCommit(Object.keys(merged).length > 0 ? merged : null);
                        }}
                    />
                </label>
            ))}
        </div>
    );
}

/** Die Spalten einer Liste: die Felder eines Objekts, sonst eine einzige. */
const columnsOf = (item: any, lang: 'de' | 'en', valueLabel: string): Array<SlotField & { key: string }> =>
    item?.valueType === PerspectiveValueType.object
        ? ((item.fields ?? []) as SlotField[]).map((field) => ({ ...field, key: field.name }))
        : [{ key: item?.valueType === PerspectiveValueType.reference ? 'ref' : 'value', name: 'value', label: { de: valueLabel, en: valueLabel }, valueType: item?.valueType ?? PerspectiveValueType.text, target: item?.target }];

/**
 * Eine Liste als Tabelle: jeder Eintrag eine Zeile, Anlegen unten, Entfernen am Eintrag.
 *
 * Entfernen fragt nach, in der Zeile selbst — der Betrachter zeigt keine Bestätigungsfenster, und ein
 * versehentlich entfernter Eintrag wäre nur über die Historie zurückzuholen.
 */
export function ListSlotEditor({
    item,
    value,
    onAdd,
    onRemove,
    onEdit,
    disabled,
}: {
    item: any;
    value: any[] | null | undefined;
    onAdd: (entry: Record<string, any>) => Promise<void>;
    onRemove: (key: string) => Promise<void>;
    onEdit: (items: any[]) => Promise<void>;
    disabled?: boolean;
}) {
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.startsWith('en') ? 'en' : 'de';
    const items = useMemo(() => (Array.isArray(value) ? value : []), [value]);
    const columns = columnsOf(item, lang, t('portfolioDashboard.slots.value'));
    const [draft, setDraft] = useState<Record<string, any>>({});
    const [confirming, setConfirming] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const draftKey = useRef(0);

    const cell = (entry: any, column: SlotField & { key: string }) => (column.key === 'ref' ? entry?.ref : column.key === 'value' ? entry?.value : entry?.[column.key]);
    const withCell = (entry: any, column: SlotField & { key: string }, next: any) =>
        column.key === 'ref' ? { ...entry, ref: next } : column.key === 'value' ? { ...entry, value: next } : { ...entry, [column.key]: next };
    const run = async (work: () => Promise<void>) => {
        setBusy(true);
        try {
            await work();
        } finally {
            setBusy(false);
        }
    };
    const draftEmpty = columns.every((column) => isBlank(cell(draft, column)));

    return (
        <div className="space-y-2">
            <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                        <tr>
                            {columns.map((column) => (
                                <th key={column.key} className="px-2 py-1 font-medium">
                                    {labelOf(column, lang)}
                                </th>
                            ))}
                            <th className="w-16" />
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length + 1} className="px-2 py-2 text-xs text-muted-foreground">
                                    {t('portfolioDashboard.slots.empty')}
                                </td>
                            </tr>
                        ) : null}
                        {items.map((entry) => (
                            <tr key={entry._key} className="border-t align-top">
                                {columns.map((column) => (
                                    <td key={column.key} className="px-2 py-1">
                                        <FieldInput
                                            field={column}
                                            value={cell(entry, column)}
                                            disabled={disabled || busy}
                                            onCommit={(next) =>
                                                void run(() => onEdit(items.map((other) => (other._key === entry._key ? withCell(other, column, next) : other))))
                                            }
                                        />
                                    </td>
                                ))}
                                <td className="px-1 py-1 text-right">
                                    {disabled ? null : confirming === entry._key ? (
                                        <span className="inline-flex gap-1">
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="destructive"
                                                className="h-7 px-2"
                                                disabled={busy}
                                                aria-label={t('portfolioDashboard.slots.confirmRemove')}
                                                onClick={() => void run(async () => {
                                                    await onRemove(entry._key);
                                                    setConfirming(null);
                                                })}
                                            >
                                                <Check className="h-3.5 w-3.5" aria-hidden />
                                            </Button>
                                            <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => setConfirming(null)} aria-label={t('global.cancel')}>
                                                <X className="h-3.5 w-3.5" aria-hidden />
                                            </Button>
                                        </span>
                                    ) : (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 px-2"
                                            aria-label={t('portfolioDashboard.slots.remove')}
                                            onClick={() => setConfirming(entry._key)}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" aria-hidden />
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {disabled ? null : (
                            <tr key={`draft-${draftKey.current}`} className="border-t bg-muted/20 align-top">
                                {columns.map((column) => (
                                    <td key={column.key} className="px-2 py-1">
                                        <FieldInput field={column} value={cell(draft, column)} disabled={busy} onCommit={(next) => setDraft((current) => withCell(current, column, next))} />
                                    </td>
                                ))}
                                <td className="px-1 py-1 text-right">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2"
                                        disabled={busy || draftEmpty}
                                        aria-label={t('portfolioDashboard.slots.add')}
                                        onClick={() => void run(async () => {
                                            await onAdd(draft);
                                            setDraft({});
                                            draftKey.current += 1;
                                        })}
                                    >
                                        <Plus className="h-3.5 w-3.5" aria-hidden />
                                    </Button>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/**
 * Ein gebundener Slot.
 *
 * Bei `one` steht der Wert im gewöhnlichen Feld darüber; hier nur, woher er kommt. Bei `many` ist dies
 * der Slot: eine Zeile je Instanz, der Wert an der Instanz bearbeitbar, die Instanz verlinkt.
 */
export function BoundSlotView({
    slot,
    resolved,
    onSaveInstance,
    disabled,
}: {
    slot: any;
    resolved: ResolvedSlot;
    onSaveInstance?: (ref: SlotReference, value: any) => Promise<void>;
    disabled?: boolean;
}) {
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.startsWith('en') ? 'en' : 'de';
    const className = (knownEntityType(resolved.className) as any)?.type?.label?.[lang] ?? resolved.className;
    const source = resolved.source?.documentName;

    if (resolved.cardinality === 'one') {
        const instance = resolved.instances[0];
        return instance ? (
            <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                <span>{t('portfolioDashboard.slots.boundTo', { className })}</span>
                <EntityLink reference={instance.ref} label={instance.label} />
                {source ? <span>· {t('portfolioDashboard.slots.fromDocument', { document: source })}</span> : null}
            </div>
        ) : null;
    }

    const editable = !disabled && !!onSaveInstance && slot.valueType !== PerspectiveValueType.list;
    return (
        <div className="space-y-1">
            <div className="overflow-x-auto rounded-md border">
                <table className="w-full table-fixed text-sm">
                    <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                        <tr>
                            <th className="w-1/3 px-2 py-1 font-medium">{className}</th>
                            <th className="px-2 py-1 font-medium">{labelOf(slot, lang)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {resolved.instances.map((instance) => (
                            <tr key={`${instance.ref.type}:${instance.ref._id}`} className="border-t align-top">
                                <td className="break-words px-2 py-1">
                                    {instance.missing ? (
                                        <span className="text-muted-foreground">{t('portfolioDashboard.slots.missing')}</span>
                                    ) : (
                                        <EntityLink reference={instance.ref} label={instance.label} />
                                    )}
                                </td>
                                <td className="break-words px-2 py-1">
                                    {editable && !instance.missing ? (
                                        slot.valueType === PerspectiveValueType.object ? (
                                            <ObjectSlotEditor fields={slot.fields ?? []} value={instance.value} onCommit={(next) => void onSaveInstance!(instance.ref, next)} />
                                        ) : (
                                            <FieldInput field={slot} value={instance.value} onCommit={(next) => void onSaveInstance!(instance.ref, next)} />
                                        )
                                    ) : (
                                        formatValue(slot, instance.value, lang)
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {source ? <div className="text-xs text-muted-foreground">{t('portfolioDashboard.slots.fromDocument', { document: source })}</div> : null}
        </div>
    );
}
